/**
 * Les HEURES DE L'ÉQUIPE saisies dans le bon (règle de Benjamin, 2026-10-24) : une ligne par technicien et par jour. Ce sont les
 * mêmes heures que celles du planning (la source unique : l'équipe de l'intervention) — le bon les écrit en s'enregistrant, il
 * n'en garde aucune copie. Elles sont toujours enregistrées (charge, rentabilité), que le bon soit sous contrat annuel ou facturé à
 * la visite : c'est le chiffrage qui dit si elles se facturent. Le palier de facturation (quart d'heure supérieur) se calcule à la
 * validation, jamais ici.
 *
 * Logique pure, testée sans navigateur.
 */
import type { HeuresEquipeBon } from '../api/client';
import { cleNomTechnicien } from './bon-edition';
import { dureeFr } from './planning';
import { lireHeuresSaisies } from './planning-interventions';

export interface HeureEditable {
  /** AAAA-MM-JJ. */
  readonly jour: string;
  /** Un nom de technicien du bon. */
  readonly technicien: string;
  /** Texte saisi : « 3 », « 1,75 », « 1h45 », « 45 min » ; vide = pas d'heures (efface celles enregistrées). */
  readonly duree: string;
  /** Vrai pour une ligne qui portait des heures enregistrées : son jour et son technicien ne changent plus (on efface la durée). */
  readonly enregistree: boolean;
  /**
   * Registre B34 : heures CALCULÉES d'après les pointages du technicien ce jour-là. Lecture seule : elles ne se tapent pas et
   * ne partent jamais avec le bon (on corrige un pointage).
   */
  readonly pointee?: boolean;
}

/** Une ligne à l'écran : une ligne d'heures, ou une ligne PROPOSÉE (un technicien du bon sans heures le jour du bon). */
export interface LigneHeures extends HeureEditable {
  readonly proposee: boolean;
}

/** « 105 » minutes → « 1h45 » ; « 120 » → « 2 » ; « 45 » → « 45 min » (des formes que la saisie relit telles quelles). */
export function texteDeMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste === 0 ? String(h) : `${h}h${String(reste).padStart(2, '0')}`;
}

/** Les heures enregistrées du bon, telles qu'on les modifie. */
export function heuresDepuisBon(heures: readonly HeuresEquipeBon[]): HeureEditable[] {
  return heures.map((h) => ({ jour: h.jour, technicien: h.technicien, duree: texteDeMinutes(h.minutes), enregistree: true, pointee: h.pointe }));
}

const cle = (jour: string, technicien: string): string => `${jour}|${cleNomTechnicien(technicien)}`;

/**
 * Les lignes à afficher : les lignes d'heures, puis, le jour du bon, une ligne proposée (vide) pour chaque technicien du bon qui n'y
 * a pas encore d'heures. Une ligne proposée n'existe qu'à l'écran tant qu'on n'y a rien tapé.
 */
export function lignesHeures(heures: readonly HeureEditable[], techniciens: readonly string[], jourDuBon: string): LigneHeures[] {
  const lignes: LigneHeures[] = heures.map((h) => ({ ...h, proposee: false }));
  if (jourDuBon !== '') {
    const vues = new Set(heures.map((h) => cle(h.jour, h.technicien)));
    for (const t of techniciens) {
      if (!vues.has(cle(jourDuBon, t))) lignes.push({ jour: jourDuBon, technicien: t, duree: '', enregistree: false, proposee: true });
    }
  }
  return lignes;
}

/** Tape une durée sur une ligne (existante ou proposée) : la ligne proposée devient une ligne d'heures. */
export function majDuree(heures: readonly HeureEditable[], jour: string, technicien: string, duree: string): HeureEditable[] {
  const k = cle(jour, technicien);
  const i = heures.findIndex((h) => cle(h.jour, h.technicien) === k);
  if (i >= 0) return heures.map((h, j) => (j === i && h.pointee !== true ? { ...h, duree } : h));
  return [...heures, { jour, technicien, duree, enregistree: false }];
}

/** Ajoute une ligne pour un autre jour (jour et technicien à choisir). */
export function ajouterLigneHeures(heures: readonly HeureEditable[], jour: string, technicien: string): HeureEditable[] {
  return [...heures, { jour, technicien, duree: '', enregistree: false }];
}

/** Change le jour ou le technicien d'une ligne AJOUTÉE (jamais d'une ligne enregistrée : on en efface la durée). */
export function majLigneAjoutee(heures: readonly HeureEditable[], index: number, changement: Partial<Pick<HeureEditable, 'jour' | 'technicien'>>): HeureEditable[] {
  return heures.map((h, i) => (i === index && !h.enregistree ? { ...h, ...changement } : h));
}

/** Retire une ligne ajoutée ; une ligne enregistrée ne se retire pas, sa durée se vide (elle sera effacée à l'enregistrement). */
export function retirerLigneHeures(heures: readonly HeureEditable[], index: number): HeureEditable[] {
  const h = heures[index];
  if (!h) return [...heures];
  if (h.pointee === true) return [...heures];
  if (h.enregistree) return heures.map((x, i) => (i === index ? { ...x, duree: '' } : x));
  return heures.filter((_, i) => i !== index);
}

/** Ce qui empêche d'enregistrer : une durée illisible, un jour à venir avec des heures, un jour ou un technicien manquant, un doublon. */
export function problemesHeures(heures: readonly HeureEditable[], aujourdhui: string): string[] {
  const problemes: string[] = [];
  const vues = new Set<string>();
  heures.forEach((h, i) => {
    const nom = h.technicien.trim() === '' ? `ligne ${i + 1}` : h.technicien;
    if (h.duree.trim() === '' && !h.enregistree) return; // une ligne vide ajoutée n'est pas envoyée
    if (!/^\d{4}-\d{2}-\d{2}$/.test(h.jour)) {
      problemes.push(`Heures (${nom}) : choisissez le jour.`);
      return;
    }
    if (h.technicien.trim() === '') {
      problemes.push(`Heures, ligne ${i + 1} : choisissez le technicien.`);
      return;
    }
    const k0 = cle(h.jour, h.technicien);
    // Heures calculées d'après les pointages : en lecture seule, jamais envoyées ; « 0 min » y est une valeur légitime.
    if (h.pointee === true) {
      vues.add(k0);
      return;
    }
    const minutes = lireHeuresSaisies(h.duree);
    if (minutes === 'ERREUR') problemes.push(`Heures de ${nom} : écrivez une durée comme 3, 7,5, 1h30 ou 45 min (de 1 minute à 24 heures).`);
    else if (minutes !== null && h.jour > aujourdhui) problemes.push(`Heures de ${nom} : on ne saisit pas d’heures pour un jour à venir.`);
    const k = cle(h.jour, h.technicien);
    if (vues.has(k)) problemes.push(`Heures de ${nom} : une seule ligne par technicien et par jour.`);
    vues.add(k);
  });
  return problemes;
}

/**
 * Ce que le bon envoie : chaque ligne d'heures (une durée vide sur une ligne enregistrée l'efface : `null`) ; une ligne ajoutée
 * restée vide n'est pas envoyée. Une durée illisible n'est jamais envoyée (les problèmes de saisie la signalent avant).
 */
export function heuresEnvoyees(heures: readonly HeureEditable[]): { jour: string; technicien: string; minutes: number | null }[] {
  const envoyees: { jour: string; technicien: string; minutes: number | null }[] = [];
  for (const h of heures) {
    // Des heures calculées d'après les pointages ne partent jamais avec le bon : le serveur les refuserait si elles changeaient.
    if (h.pointee === true) continue;
    if (h.technicien.trim() === '' || !/^\d{4}-\d{2}-\d{2}$/.test(h.jour)) continue;
    const minutes = lireHeuresSaisies(h.duree);
    if (minutes === 'ERREUR') continue;
    if (minutes === null && !h.enregistree) continue;
    envoyees.push({ jour: h.jour, technicien: h.technicien.trim(), minutes });
  }
  return envoyees;
}

/** Le total des heures saisies lisibles, en minutes (pour la ligne « Total »). */
export function totalMinutes(heures: readonly HeureEditable[]): number {
  let total = 0;
  for (const h of heures) {
    const m = lireHeuresSaisies(h.duree);
    if (typeof m === 'number') total += m;
  }
  return total;
}

/**
 * Ce que l'heure saisie donnera, facturée au quart d'heure supérieur (« 1,75 » → « 1 h 45 », « 1h40 » → « 1 h 45 »). Une indication :
 * le serveur facture au palier réglé par l'espace (15 minutes par défaut), à la validation. Rien pour une durée vide ou illisible.
 */
export function quartDHeure(lecture: number | null | 'ERREUR'): string {
  if (typeof lecture !== 'number') return '';
  return dureeFr(Math.ceil(lecture / 15) * 15);
}
